import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  CalendarRange,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Users,
  BookOpen,
  Lock
} from 'lucide-react';
import apiClient from '../services/apiClient';

/**
 * TeacherSectionSettingsPage
 *
 * Configuración de una sección activa. Funcionalidades:
 * - Ver y cambiar la semana activa (currentWeek) de la sección
 * - Resumen de estado de la sección
 * - Acceso rápido al monitor de semana
 *
 * Route: /teacher/:sectionId/settings
 *
 * Backend:
 *   GET  /api/sections/:sectionId  → CourseSection con currentWeek
 *   PUT  /api/sections/:sectionId  { currentWeek }
 */
const TeacherSectionSettingsPage = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  // Datos de la sección (CourseSection)
  const [sectionMeta, setSectionMeta] = useState(null);
  const [groupCount, setGroupCount] = useState(null);

  // Control de semana
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [confirmingWeekChange, setConfirmingWeekChange] = useState(false);

  const TOTAL_WEEKS = 18;

  useEffect(() => {
    loadData();
  }, [sectionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiClient.get(`/sections/${sectionId}`);
      if (res.data?.section) {
        setSectionMeta(res.data.section);
        setGroupCount(res.data.groupCount ?? null);
        setSelectedWeek(res.data.section.currentWeek || 1);
      } else {
        setError('No se encontró la sección o no tienes permisos sobre ella.');
      }
    } catch (err) {
      setError('Error al cargar la configuración: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleWeekChange = (delta) => {
    setSelectedWeek(prev => {
      const next = prev + delta;
      if (next < 1 || next > TOTAL_WEEKS) return prev;
      return next;
    });
    setConfirmingWeekChange(false);
    setSuccessMsg(null);
  };

  const handleSaveWeek = async () => {
    if (!confirmingWeekChange) {
      setConfirmingWeekChange(true);
      return;
    }
    try {
      setSaving(true);
      setSuccessMsg(null);
      await apiClient.put(`/sections/${sectionId}`, { currentWeek: selectedWeek });
      setSectionMeta(prev => ({ ...prev, currentWeek: selectedWeek }));
      setConfirmingWeekChange(false);
      setSuccessMsg(`Semana activa actualizada a la semana ${selectedWeek}.`);
    } catch (err) {
      setError('Error al guardar: ' + (err.response?.data?.message || err.message));
      setConfirmingWeekChange(false);
    } finally {
      setSaving(false);
    }
  };

  const hasWeekChanged = sectionMeta && selectedWeek !== (sectionMeta.currentWeek || 1);
  const sectionName = sectionMeta?.name || sectionId;
  const courseName = sectionMeta?.courseId || '';

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando configuración...</span>
        </div>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (error && !sectionMeta) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  // ─── Main ──────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            Configuración de sección
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{sectionName}</p>
        </div>
      </div>

      {/* Resumen de la sección */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Información general</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Curso</p>
              <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">{courseName || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Grupos</p>
              <p className="text-sm font-medium text-gray-900">{groupCount !== null ? groupCount : '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Lock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Estado</p>
              <p className="text-sm font-medium text-gray-900 capitalize">{sectionMeta?.status || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Control de semana activa */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Semana activa</h2>
        <p className="text-xs text-gray-400 mb-5">
          Los estudiantes solo pueden acceder a semanas ≤ a la semana activa.
          Aumentar la semana activa habilita acceso a la siguiente semana para todos los grupos.
        </p>

        {/* Selector visual */}
        <div className="flex items-center justify-center gap-6 py-4">
          <button
            onClick={() => handleWeekChange(-1)}
            disabled={selectedWeek <= 1}
            className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>

          <div className="text-center">
            <div className="text-6xl font-bold text-blue-600 tabular-nums leading-none">
              {selectedWeek}
            </div>
            <div className="text-sm text-gray-400 mt-1">de {TOTAL_WEEKS} semanas</div>
          </div>

          <button
            onClick={() => handleWeekChange(1)}
            disabled={selectedWeek >= TOTAL_WEEKS}
            className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Barra de progreso */}
        <div className="mt-3 mb-5">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Semana 1</span>
            <span>Semana {TOTAL_WEEKS}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-2 bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${((selectedWeek - 1) / (TOTAL_WEEKS - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Estado actual vs seleccionado */}
        {sectionMeta && (
          <div className="flex items-center justify-center gap-2 text-sm mb-5">
            <span className="text-gray-500">Activa actualmente:</span>
            <span className="font-semibold text-gray-900">Semana {sectionMeta.currentWeek || 1}</span>
            {hasWeekChanged && (
              <>
                <ChevronRight className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-blue-600">Semana {selectedWeek}</span>
              </>
            )}
          </div>
        )}

        {/* Confirmación y error inline */}
        {error && sectionMeta && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{successMsg}</p>
          </div>
        )}

        {confirmingWeekChange && hasWeekChanged && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 mb-1">¿Confirmar cambio?</p>
            <p className="text-xs text-amber-700">
              Todos los grupos de esta sección pasarán a tener acceso hasta la <strong>semana {selectedWeek}</strong>.
              {selectedWeek > (sectionMeta.currentWeek || 1)
                ? ' Se habilitará acceso a nuevas semanas.'
                : ' Se restringirá el acceso a semanas superiores.'}
            </p>
          </div>
        )}

        {/* Botón guardar */}
        <div className="flex justify-end gap-3">
          {confirmingWeekChange && (
            <button
              onClick={() => { setConfirmingWeekChange(false); setSelectedWeek(sectionMeta.currentWeek || 1); }}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
          )}
          <button
            onClick={handleSaveWeek}
            disabled={!hasWeekChanged || saving}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors
              ${confirmingWeekChange
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed'
              }`}
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</>
            ) : confirmingWeekChange ? (
              <><CheckCircle className="w-4 h-4" /> Confirmar cambio</>
            ) : (
              <>Aplicar semana {selectedWeek}</>
            )}
          </button>
        </div>
      </div>

      {/* Acceso rápido */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Accesos rápidos</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/teacher/${sectionId}/week/${sectionMeta?.currentWeek || 1}`)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm font-medium transition-colors"
          >
            <CalendarRange className="w-4 h-4" />
            Monitor semana activa (S{sectionMeta?.currentWeek || 1})
          </button>
          <button
            onClick={() => navigate(`/teacher/${sectionId}/groups`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium transition-colors"
          >
            <Users className="w-4 h-4" />
            Gestión de grupos
          </button>
        </div>
      </div>

    </div>
  );
};

export default TeacherSectionSettingsPage;
